import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Clock, CheckCircle, XCircle, Loader2, Trophy, Award, AlertTriangle, Lock, KeyRound } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FinalExamPage = () => {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const { token, loading: authLoading } = useAuth();
  
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(60 * 60);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  
  // Password protection states
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [verifyingPassword, setVerifyingPassword] = useState(false);
  const handleSubmitRef = useRef(null);

  const fetchExam = useCallback(async (examPassword = null) => {
    try {
      if (examPassword) {
        // Password provided - use the access endpoint to get questions
        const response = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/final-exam/access`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ password: examPassword })
        });
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Failed to access final exam');
        }
        
        const data = await response.json();
        setNeedsPassword(false);
        setExam(data);
        setTimeLeft(data.duration_minutes * 60);
      } else {
        // No password - fetch exam info first
        const response = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/final-exam`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Failed to load final exam');
        }
        
        const data = await response.json();
        
        if (!data.is_active) {
          toast.error('Final exam is not currently active');
          navigate('/dashboard');
          return;
        }
        
        if (data.is_password_protected) {
          setNeedsPassword(true);
          setExam(data);
          setLoading(false);
          return;
        }
        
        // Not password protected - access directly with empty password
        const accessResponse = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/final-exam/access`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ password: '' })
        });
        
        if (!accessResponse.ok) {
          const accessData = await accessResponse.json();
          throw new Error(accessData.detail || 'Failed to access final exam');
        }
        
        const accessData = await accessResponse.json();
        setNeedsPassword(false);
        setExam(accessData);
        setTimeLeft(accessData.duration_minutes * 60);
      }
    } catch (error) {
      toast.error(error.message);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [schoolId, token, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!token) {
      navigate('/login');
      return;
    }
    fetchExam();
  }, [token, authLoading, navigate, fetchExam]);

  useEffect(() => {
    if (!exam || result || needsPassword) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmitRef.current?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [exam, result, needsPassword]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setVerifyingPassword(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/final-exam/access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ password })
      });
      
      if (!response.ok) {
        const data = await response.json();
        setPasswordError(data.detail || 'Incorrect password');
        return;
      }
      
      const data = await response.json();
      setNeedsPassword(false);
      setExam(data);
      setTimeLeft(data.duration_minutes * 60);
      setLoading(false);
    } catch (error) {
      setPasswordError('Failed to verify password. Please try again.');
    } finally {
      setVerifyingPassword(false);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setShowConfirmSubmit(false);
    try {
      const response = await fetch(`${BACKEND_URL}/api/schools/${schoolId}/final-exam/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          exam_id: `${schoolId}-final`,
          answers: answers
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit exam');
      }

      setResult(data);
      toast.success(data.passed ? 'Congratulations! You passed the Final Exam!' : 'Final Exam submitted');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  }, [schoolId, token, answers]);

  useEffect(() => { handleSubmitRef.current = handleSubmit; }, [handleSubmit]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Show loading while auth context is loading or exam data is loading
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#5f7c8a]" />
      </div>
    );
  }

  // Password Entry Screen
  if (needsPassword) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        
        <main className="flex-1 pt-20 pb-12 px-4 flex items-center justify-center">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto p-4 rounded-full bg-green-100 mb-4">
                <Lock size={48} className="text-green-600" />
              </div>
              <Badge className="mx-auto mb-2 bg-green-600">Password Protected</Badge>
              <CardTitle className="text-xl">{exam?.title}</CardTitle>
              <CardDescription>
                This exam requires a password to access. Please enter the exam password provided by your instructor.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Exam Password</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter exam password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                  {passwordError && (
                    <p className="text-sm text-red-600">{passwordError}</p>
                  )}
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-600">
                  <p><strong>Exam Details:</strong></p>
                  <p>• {exam?.question_count} Questions</p>
                  <p>• {exam?.duration_minutes} Minutes</p>
                  <p>• Passing Score: {exam?.passing_score}%</p>
                </div>
                
                <div className="flex gap-3">
                  <Button 
                    type="button"
                    variant="outline" 
                    className="flex-1"
                    onClick={() => navigate('/dashboard')}
                  >
                    Back to Dashboard
                  </Button>
                  <Button 
                    type="submit"
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={verifyingPassword || !password}
                  >
                    {verifyingPassword ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...</>
                    ) : (
                      <><Lock size={16} className="mr-2" /> Unlock Exam</>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </main>

        <Footer />
      </div>
    );
  }

  // Result Screen
  if (result) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        
        <main className="flex-1 pt-20 pb-12 px-4 flex items-center justify-center">
          <Card className="max-w-md w-full text-center">
            <CardHeader>
              <Badge className="mx-auto mb-4 bg-green-600">Final Exam</Badge>
              <div className={`mx-auto p-4 rounded-full ${result.passed ? 'bg-green-100' : 'bg-red-100'} mb-4`}>
                {result.passed ? (
                  <Trophy size={48} className="text-green-600" />
                ) : (
                  <XCircle size={48} className="text-red-600" />
                )}
              </div>
              <CardTitle className="text-2xl">
                {result.passed ? 'Congratulations!' : 'Keep Learning!'}
              </CardTitle>
              <CardDescription>
                {result.passed ? 'You have passed the Final Exam!' : 'You can retake this exam after more preparation.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-3xl font-bold text-green-600">{result.score.toFixed(0)}%</p>
                  <p className="text-sm text-gray-500">Final Score</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-3xl font-bold text-[#5f7c8a]">{result.correct_answers}/{result.total_questions}</p>
                  <p className="text-sm text-gray-500">Correct</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => navigate('/dashboard')}
                >
                  Back to Dashboard
                </Button>
                {!result.passed && (
                  <Button 
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      setResult(null);
                      setAnswers({});
                      setCurrentQuestion(0);
                      setNeedsPassword(false);
                      setPassword('');
                      fetchExam();
                    }}
                  >
                    Retake Final Exam
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </main>

        <Footer />
      </div>
    );
  }

  // Confirm Submit Dialog
  if (showConfirmSubmit) {
    const unanswered = exam.questions.length - Object.keys(answers).length;
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        
        <main className="flex-1 pt-20 pb-12 px-4 flex items-center justify-center">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto p-4 rounded-full bg-yellow-100 mb-4">
                <AlertTriangle size={48} className="text-yellow-600" />
              </div>
              <CardTitle>Submit Final Exam?</CardTitle>
              <CardDescription>
                This action cannot be undone.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <p className="text-lg font-medium">{Object.keys(answers).length} of {exam.questions.length} questions answered</p>
                {unanswered > 0 && (
                  <p className="text-sm text-yellow-600 mt-1">
                    {unanswered} question{unanswered > 1 ? 's' : ''} unanswered
                  </p>
                )}
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowConfirmSubmit(false)}
                >
                  Continue Exam
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                  ) : (
                    'Submit Final Exam'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>

        <Footer />
      </div>
    );
  }

  const question = exam?.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / exam?.questions.length) * 100;
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-1 pt-20 pb-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Exam Header */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="bg-green-600">
                    <Award size={12} className="mr-1" /> Final Exam
                  </Badge>
                </div>
                <h1 className="text-xl font-bold text-gray-800">{exam?.title}</h1>
                <p className="text-gray-500">{exam?.school_name}</p>
              </div>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${timeLeft < 600 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                <Clock size={20} />
                <span className="font-mono font-bold">{formatTime(timeLeft)}</span>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-500 mb-2">
                <span>Question {currentQuestion + 1} of {exam?.questions.length}</span>
                <span>{answeredCount} answered</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </div>

          {/* Question Card */}
          <Card className="mb-6 border-2 border-green-200">
            <CardHeader>
              <div className="flex items-start gap-4">
                <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  Q{currentQuestion + 1}
                </span>
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide">
                    {question?.question_type === 'true_false' ? 'True/False' : 'Multiple Choice'}
                  </span>
                  <CardTitle className="text-lg mt-1">{question?.question_text}</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={answers[question?.id] || ''}
                onValueChange={(value) => handleAnswerChange(question?.id, value)}
                className="space-y-3"
              >
                {question?.options.map((option, index) => (
                  <div 
                    key={index}
                    className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                      answers[question?.id] === option 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleAnswerChange(question?.id, option)}
                  >
                    <RadioGroupItem value={option} id={`option-${index}`} />
                    <Label htmlFor={`option-${index}`} className="flex-1 cursor-pointer">
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={() => setCurrentQuestion(prev => prev - 1)}
              disabled={currentQuestion === 0}
            >
              <ArrowLeft size={16} className="mr-2" /> Previous
            </Button>

            <div className="flex gap-2 flex-wrap justify-center max-w-md">
              {exam?.questions.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentQuestion(index)}
                  className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                    currentQuestion === index
                      ? 'bg-green-600 text-white'
                      : answers[exam?.questions[index]?.id]
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>

            {currentQuestion === exam?.questions.length - 1 ? (
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={() => setShowConfirmSubmit(true)}
                disabled={submitting}
              >
                {submitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                ) : (
                  <><CheckCircle size={16} className="mr-2" /> Submit Final Exam</>
                )}
              </Button>
            ) : (
              <Button
                onClick={() => setCurrentQuestion(prev => prev + 1)}
                className="bg-green-600 hover:bg-green-700"
              >
                Next <ArrowRight size={16} className="ml-2" />
              </Button>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FinalExamPage;
